import { useEffect, useCallback, useRef } from 'react';
import { supabaseData } from '../../utils/supabase-data-client';
import { useCacheStore } from './useCacheStore';
import { Category, Bookmark } from '../../types/bookmarks';
import { NoteCategory, Note, NoteTemplate } from '../../types/notes';
import { CACHE_UPDATED_EVENT } from './useCacheFirstQuery';

/**
 * useBackgroundSync: Subscribes to Supabase Realtime for live updates
 * on bookmarks and bookmark_categories tables. On any change event,
 * refetches the full dataset and updates the cache silently.
 *
 * Also handles visibilitychange (tab/app comes to foreground) to catch
 * up on events that may have been missed while backgrounded.
 */
const useBackgroundSync = () => {
  const cache = useCacheStore();
  const isFetching = useRef(false);

  const refetchAll = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;

    try {
      const [
        categoriesResult,
        bookmarksResult,
        noteCategoriesResult,
        notesResult,
        noteTemplatesResult,
      ] = await Promise.all([
        supabaseData
          .from('bookmark_categories')
          .select('*')
          .order('category_name'),
        supabaseData.from('bookmarks').select('*').order('bookmark_name'),
        supabaseData.from('note_categories').select('*').order('category_name'),
        supabaseData
          .from('notes')
          .select('*')
          .is('deleted_at', null)
          .order('note_title'),
        supabaseData.from('note_templates').select('*').order('template_name'),
      ]);

      if (!categoriesResult.error && categoriesResult.data) {
        cache.setCategories(categoriesResult.data as Category[]);
      }
      if (!bookmarksResult.error && bookmarksResult.data) {
        cache.setBookmarks(bookmarksResult.data as Bookmark[]);
      }
      if (!noteCategoriesResult.error && noteCategoriesResult.data) {
        cache.setNoteCategories(noteCategoriesResult.data as NoteCategory[]);
      }
      if (!notesResult.error && notesResult.data) {
        cache.setNotes(notesResult.data as Note[]);
      }
      if (!noteTemplatesResult.error && noteTemplatesResult.data) {
        cache.setNoteTemplates(noteTemplatesResult.data as NoteTemplate[]);
      }

      // Dispatch cache updated event so useSearch and other listeners rebuild
      window.dispatchEvent(new Event(CACHE_UPDATED_EVENT));
    } catch {
      // Network failure — silently continue with cached data
    } finally {
      isFetching.current = false;
    }
  }, [cache]);

  useEffect(() => {
    // Subscribe to Realtime changes on bookmarks table
    const bookmarksChannel = supabaseData
      .channel('bookmarks-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'bookmarks' },
        () => {
          refetchAll();
        },
      )
      .subscribe();

    // Subscribe to Realtime changes on bookmark_categories table
    const categoriesChannel = supabaseData
      .channel('categories-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'bookmark_categories' },
        () => {
          refetchAll();
        },
      )
      .subscribe();

    // Subscribe to Realtime changes on the notes tables
    const notesChannel = supabaseData
      .channel('notes-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notes' },
        () => {
          refetchAll();
        },
      )
      .subscribe();

    const noteCategoriesChannel = supabaseData
      .channel('note-categories-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'note_categories' },
        () => {
          refetchAll();
        },
      )
      .subscribe();

    const noteTemplatesChannel = supabaseData
      .channel('note-templates-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'note_templates' },
        () => {
          refetchAll();
        },
      )
      .subscribe();

    // Catch up on visibility change (tab/app comes back to foreground)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetchAll();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      supabaseData.removeChannel(bookmarksChannel);
      supabaseData.removeChannel(categoriesChannel);
      supabaseData.removeChannel(notesChannel);
      supabaseData.removeChannel(noteCategoriesChannel);
      supabaseData.removeChannel(noteTemplatesChannel);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetchAll]);
};

export { useBackgroundSync };
